"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { Check, FileText, Sparkles } from "lucide-react";
import { MockChrome } from "./MockWindow";

/**
 * Auto-looping "draft the form from the protocol" demo.
 *
 * The protocol section goes in, fields and edit checks come out, and the
 * result lands as a draft that a data manager still has to approve. That last
 * beat is the point of the whole animation, not a footnote: a clinical data
 * manager reviewing the product was explicit that AI output has to be treated
 * as a proposal, never as a validated study design. So the timeline ends on
 * "awaiting review", and the approve row is the only green thing on screen.
 *
 * Pairs with DragDropShowcase, which picks up where this stops — the draft is
 * the starting point, and anything in it can be changed by hand.
 */

type Field = { label: string; type: string; source: string };

const FIELDS: Field[] = [
  { label: "Verbatim term", type: "Text", source: "§7.2" },
  { label: "Start date", type: "Date", source: "§7.2" },
  { label: "Severity", type: "Select", source: "§7.4" },
  { label: "Related to drug", type: "Yes / No", source: "§7.5" },
];

const CHECKS = [
  "Start date cannot be in the future",
  "Severity Grade 3+ opens the SAE form",
];

export function AiFormBuilderShowcase() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const reset = () => {
        gsap.set(".ai-thinking", { opacity: 0 });
        gsap.set(".ai-field", { opacity: 0, y: 6 });
        gsap.set(".ai-check", { opacity: 0, y: 4 });
        gsap.set(".ai-status-draft", { opacity: 0 });
        gsap.set(".ai-status-idle", { opacity: 1 });
        gsap.set(".ai-approve", { opacity: 0, y: 8 });
        gsap.set(".ai-send", { scale: 1 });
      };
      reset();

      const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.6, onRepeat: reset });

      tl
        // the prompt is sent
        .to(".ai-send", { scale: 0.94, duration: 0.12 }, 0.6)
        .to(".ai-send", { scale: 1, duration: 0.18 }, 0.72)
        .to(".ai-status-idle", { opacity: 0, duration: 0.2 }, 0.72)
        .to(".ai-thinking", { opacity: 1, duration: 0.25 }, 0.8)
        // fields arrive one at a time, in protocol order
        .to(".ai-field", { opacity: 1, y: 0, duration: 0.32, stagger: 0.16, ease: "power2.out" }, 1.5)
        // then the checks it derived from them
        .to(".ai-check", { opacity: 1, y: 0, duration: 0.3, stagger: 0.14, ease: "power2.out" }, 2.5)
        // and it stops, because the next step is a person
        .to(".ai-thinking", { opacity: 0, duration: 0.2 }, 3.1)
        .to(".ai-status-draft", { opacity: 1, duration: 0.3 }, 3.15)
        .to(".ai-approve", { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, 3.35)
        .to({}, { duration: 2.2 });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={root}
      className="overflow-hidden rounded-2xl border border-border/60 bg-surface shadow-[var(--shadow-card)]"
      role="img"
      aria-label="Animated demo: a protocol section is sent to the form builder, which drafts four case report form fields and two edit checks, then marks the result as a draft awaiting a data manager's approval."
    >
      <MockChrome title="eCRF Builder · AI draft" />

      <div className="overflow-x-auto p-3 text-[11px] xs:p-4">
        {/* the prompt */}
        <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-surface-muted/50 px-2.5 py-2">
          <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-foreground/85">
            Protocol §7 — Adverse Events
          </span>
          <span className="ai-send inline-flex shrink-0 items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10px] font-semibold text-primary-foreground">
            <Sparkles className="h-3 w-3" />
            Draft form
          </span>
        </div>

        {/* status line */}
        <div className="relative mt-2 h-4">
          <span className="ai-status-idle absolute inset-0 text-[10px] text-muted-foreground">
            Reads the protocol, proposes the form.
          </span>
          <span className="ai-thinking absolute inset-0 inline-flex items-center gap-1.5 text-[10px] font-medium text-primary">
            <span className="h-1 w-1 animate-pulse rounded-full bg-primary" />
            Drafting fields and edit checks…
          </span>
          <span className="ai-status-draft absolute inset-0 text-[10px] font-semibold text-[#7a3b00]">
            Draft · nothing is live until a data manager approves it
          </span>
        </div>

        {/* what it proposed */}
        <div className="mt-2 space-y-1">
          {FIELDS.map((f) => (
            <div
              key={f.label}
              className="ai-field flex items-center justify-between gap-2 rounded-md border border-border/50 bg-surface px-2 py-1.5 xs:px-2.5"
            >
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="h-1 w-1 shrink-0 rounded-full bg-primary/60" />
                <span className="truncate font-medium text-foreground/90">{f.label}</span>
                <span className="hidden shrink-0 font-mono text-[9px] text-muted-foreground xs:inline">
                  {f.source}
                </span>
              </div>
              <span className="ml-1 shrink-0 rounded-sm border border-border/70 bg-surface px-1 py-px font-mono text-[9px] text-muted-foreground xs:px-1.5">
                {f.type}
              </span>
            </div>
          ))}
        </div>

        {/* checks it derived */}
        <div className="mt-2 space-y-1">
          {CHECKS.map((c) => (
            <div
              key={c}
              className="ai-check flex items-start gap-1.5 rounded-md bg-primary/[0.06] px-2 py-1.5 text-[10px] text-foreground/80"
            >
              <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-primary/60" />
              <span className="min-w-0">{c}</span>
            </div>
          ))}
        </div>

        {/* the human step */}
        <div className="ai-approve mt-2.5 flex items-center justify-between gap-2 rounded-md border border-success/30 bg-success/[0.07] px-2.5 py-1.5">
          <span className="min-w-0 truncate text-[10px] text-foreground/80">
            Reviewed by a data manager
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/15 px-1.5 py-px text-[9px] font-semibold text-success">
            <Check className="h-2.5 w-2.5" />
            Approve
          </span>
        </div>
      </div>
    </div>
  );
}
